<template>
    <div class="map-wrapper" :style="sinterMapContainerStyle">
        <div id="sinter-map" class="map">
            <MapLayerSwitcher
                :map="mapRef"
                :disable-change="menuRouteModalVisible"
                v-if="!!mapRef"
                :layer-switcher-data="layerSwitcherData"
                @update-layer-data="handleLayerSwitcherUpdate"
            />
        </div>

        <!-- Ícones dos submenus do menu Sinter (canto superior esquerdo, estilo Cemitérios/HomeMap) -->
        <div class="map-icons sinter-map-icons">
            <a-button
                v-for="item in sinterMenuItems"
                :key="item.id"
                class="map-icon-btn"
                :title="item.name"
                :aria-label="item.name"
                @click="openMenuRouteModal(item)"
            >
                <template #icon>
                    <component
                        v-if="item.iconComponent"
                        :is="item.iconComponent"
                        class="menu-icon sinter-submenu-antd-icon"
                    />
                    <i
                        v-else
                        :class="['fas', item.faIcon, 'menu-icon']"
                        aria-hidden="true"
                    />
                </template>
            </a-button>
        </div>

        <a-modal
            v-model:open="menuRouteModalVisible"
            :title="menuRouteModalData.name"
            :footer="null"
            width="90%"
            wrap-class-name="menu-route-modal-wrap"
            :body-style="{ position: 'relative', maxHeight: '75vh', overflow: 'hidden', padding: 0 }"
        >
            <div class="menu-route-modal-scroll">
                <template v-if="menuRouteModalData.componentName">
                    <div class="menu-route-component-wrapper">
                        <component :is="menuRouteModalData.componentName" />
                    </div>
                </template>
                <template v-else>
                    <a-descriptions bordered size="small" :column="1">
                        <a-descriptions-item label="Menu">{{ menuRouteModalData.name }}</a-descriptions-item>
                        <a-descriptions-item label="Rota cadastrada">
                            {{ menuRouteModalData.route || '—' }}
                        </a-descriptions-item>
                    </a-descriptions>
                    <p v-if="!menuRouteModalData.route" class="menu-route-no-component">
                        Nenhum componente cadastrado para esta rota.
                    </p>
                </template>
            </div>
            <a-tooltip
                v-if="menuRouteModalData.href"
                :title="menuRouteModalData.componentName ? 'Abrir em página completa' : 'Abrir rota'"
                placement="left"
            >
                <a-button
                    type="primary"
                    shape="circle"
                    class="menu-route-float-btn"
                    @click="goToMenuRoute(menuRouteModalData.href)"
                >
                    <template #icon>
                        <ExportOutlined />
                    </template>
                </a-button>
            </a-tooltip>
        </a-modal>
    </div>
</template>

<script setup>
import { onMounted, ref, computed, nextTick } from 'vue'
import { ExportOutlined } from '@ant-design/icons-vue'
import 'ol/ol.css'

import OlMap from 'ol/Map'
import View from 'ol/View'
import { defaults as defaultControls } from 'ol/control'
import { scaleLine } from '../../../open-layers/default-controls.js'
import { antdIconsMap } from '../../../utils/antdIcons'
import { getRegisteredLayers } from '../../../open-layers/map-helpers/home.js'
import { getDefaultMapView } from '../../../open-layers/map-defaults.js'
import MapLayerSwitcher from '../MapsOpenLayers/Components/MapLayerSwitcher.vue'

const minHomeIconSize = 16
const maxHomeIconSize = 48
const homeIconSize = ref(32)
const sinterMapContainerStyle = computed(() => ({
    '--home-map-icon-size': `${homeIconSize.value}px`,
    '--home-map-icon-glyph-size': `${Math.round(homeIconSize.value * 0.5)}px`,
    '--home-map-icon-radius': `${Math.max(4, Math.round((homeIconSize.value / 32) * 6))}px`,
}))

let map = null
const mapRef = ref(null)
const layerSwitcherData = ref(null)

const sinterSubmenus = ref([])
const sinterParentMenu = ref(null)

function resolveSinterSubmenuIconComponent(child) {
    if (child.icon && antdIconsMap[child.icon]) {
        return antdIconsMap[child.icon]
    }
    const p = sinterParentMenu.value
    if (p?.icon && antdIconsMap[p.icon]) {
        return antdIconsMap[p.icon]
    }
    return null
}

const ICON_BY_ROUTE = {
    sinter: 'fa-globe',
    'sinter/domain-tables': 'fa-table',
}

const ICON_BY_NAME = {
    'Tabelas de Código': 'fa-table',
    Sinter: 'fa-globe',
}

const sinterMenuItems = computed(() => {
    const items = []
    const subs = (sinterSubmenus.value || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0))
    subs.forEach((child) => {
        const route = child.route || ''
        if (!route) return
        const href = `/${route}`
        const iconComponent = resolveSinterSubmenuIconComponent(child)
        const faIcon =
            iconComponent == null
                ? ICON_BY_ROUTE[route] || ICON_BY_NAME[child.name] || 'fa-folder'
                : null
        items.push({
            id: child.id,
            name: child.name,
            route,
            href,
            iconComponent,
            faIcon,
        })
    })
    return items
})

const ROUTE_TO_COMPONENT = {
    'sinter/domain-tables': 'sinter-tabelas-codigo',
}

const menuRouteModalVisible = ref(false)
const menuRouteModalData = ref({ name: '', route: '', href: '', componentName: '' })

function openMenuRouteModal(item) {
    const route = item.route || ''
    menuRouteModalData.value = {
        name: item.name,
        route,
        href: item.href || '',
        componentName: ROUTE_TO_COMPONENT[route] || '',
    }
    menuRouteModalVisible.value = true
}

function goToMenuRoute(href) {
    if (href) window.location.href = href
}

function handleLayerSwitcherUpdate(data) {
    layerSwitcherData.value = {
        ...data,
        updateKey: Date.now(),
    }
}

async function loadHomeIconPersonalization() {
    try {
        const { data } = await window.axios.get('/api/configurations/personalizations')
        const value = Number(data?.data?.home_icon_size)
        if (Number.isFinite(value)) {
            homeIconSize.value = Math.min(maxHomeIconSize, Math.max(minHomeIconSize, value))
        }
    } catch (_) {
        // keep default
    }
}

async function fetchSinterSubmenus() {
    try {
        const { data } = await window.axios.get('/api/configurations/menus?active=true')
        const menus = data?.data ?? []
        const sinterMenu = menus.find((m) => m.route === 'sinter' || (m.name && m.name === 'Sinter'))
        sinterParentMenu.value = sinterMenu
            ? { id: sinterMenu.id, icon: sinterMenu.icon }
            : null
        sinterSubmenus.value = sinterMenu?.childrens ?? []
    } catch (_) {
        sinterSubmenus.value = []
        sinterParentMenu.value = null
    }
}

onMounted(async () => {
    await loadHomeIconPersonalization()

    map = new OlMap({
        target: 'sinter-map',
        layers: [],
        view: new View({
            ...getDefaultMapView(),
        }),
        controls: defaultControls({ rotate: false, zoom: false }).extend([scaleLine]),
    })

    const moduleLayers = await getRegisteredLayers('Sinter')
    ;(moduleLayers || []).forEach((layer) => {
        if (layer) map.addLayer(layer)
    })

    await fetchSinterSubmenus()

    mapRef.value = map
    await nextTick()
})
</script>

<style scoped>
.map-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
.map {
    width: 100%;
    height: 100%;
}
::v-deep(.ol-zoom) {
    position: absolute;
    right: 10px;
    left: auto !important;
    z-index: 1000;
}
::v-deep(.ol-layerswitcher) {
    position: absolute;
    top: 22px;
    right: 8px;
}

.sinter-map-icons {
    position: absolute;
    top: calc(8px + var(--home-map-icon-size, 32px) + 12px);
    left: 8px;
    z-index: 1000;
}

.map-icons {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.map-icon-btn {
    width: var(--home-map-icon-size, 32px);
    height: var(--home-map-icon-size, 32px);
    box-sizing: border-box;
    border-radius: var(--home-map-icon-radius, 6px);
    background-color: rgba(0, 60, 136, 0.6);
    color: white;
    border: 2px solid white;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.map-icon-btn:hover {
    background-color: rgba(0, 60, 136, 0.7);
}

.menu-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.sinter-submenu-antd-icon {
    color: white;
    font-size: var(--home-map-icon-glyph-size, 16px);
}

.menu-route-component-wrapper {
    min-height: 120px;
}

.menu-route-no-component {
    margin: 12px 0;
    color: rgba(0, 0, 0, 0.45);
}
</style>

<style>
.menu-route-modal-wrap .menu-route-modal-scroll {
    max-height: 75vh;
    overflow: auto;
    padding: 16px;
    padding-bottom: 72px;
}

.menu-route-modal-wrap .menu-route-float-btn {
    position: absolute;
    right: 20px;
    bottom: 20px;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
</style>
